import {useContext, useEffect, useState} from 'react';

import {FormikProvider, useFormik} from 'formik';
import {useTranslation} from 'react-i18next';

import {Box, Chip} from '@mui/material';

import {AboutTheJobFields} from './AboutTheJobFields/AboutTheJobFields';
import {AssignedCompanyAdmins} from './AssignedCompanyAdmins';
import {HardFactsFields} from './HardFactsFields/HardFactsFields';
import {JobPostingFormMenu} from './JobPostingFormMenu';
import {validateJobPostingForm} from './JobPostingFormValidation';
import {EmpionPaper} from '../../../../../../../components/EmpionPaper/EmpionPaper';
import {LoadingOverlay} from '../../../../../../../components/Loading/LoadingOverlay';
import {CompanyContext} from '../../../../../../../contexts/CompanyContext/CompanyContext';
import {LoadingContext} from '../../../../../../../contexts/LoadingContext/LoadingContext';
import {SnackBarContext} from '../../../../../../../contexts/SnackBarContext/SnackBarContext';
import {useQueryParameterState} from '../../../../../../../hooks/useQueryParameterState';
import {greenColors, orangeColors} from '../../../../../../../layouts/theme/designSystem';
import {ApiResponse} from '../../../../../../../lib/api/v1/types';
import {FORM_MODE_CREATE, FORM_MODE_EDIT} from '../../../../../../../lib/constants';
import {JobPostingService} from '../../../services/JobPostingService';
import {CompanyLocation, JobPosting} from '../../../types/JobPostingsTypes';
import {AssignAdminsProps} from '../../ListView/ButtonActions/MenuButtonActions';

interface JobPostingFormProps {
  companyId: string;
  companyLocations: CompanyLocation[];
  onCancel(): void;
  afterSubmit(newJobPosting: JobPosting): void;
  jobPosting?: JobPosting;
  handleUpdateJobPosting?(jobPosting: JobPosting): void;
}

export const JobPostingForm = ({
  companyId,
  companyLocations,
  onCancel,
  afterSubmit,
  jobPosting,
  handleUpdateJobPosting,
}: JobPostingFormProps) => {
  const {t} = useTranslation();
  const {dispatchMessage} = useContext(SnackBarContext);

  const [mode, setMode] = useState<string>(jobPosting?.resourceId ? FORM_MODE_EDIT : FORM_MODE_CREATE);
  const [saveAndPublish, setSaveAndPublish] = useState<boolean>(false);
  const [jobPostingResourceId, setJobPostingResourceId] = useState<string>(jobPosting?.resourceId || '');
  const [jobPostingPublished, setJobPostingPublished] = useState<boolean>(!!jobPosting?.publishedAt);
  const [companyAdminsModal, setCompanyAdminsModal] = useState<boolean>(false);
  const {setLoadingContext} = useContext(LoadingContext);

  const jobPostingService = new JobPostingService();

  const {jobPostings} = useContext(CompanyContext);

  const {params, updateQueryParameters} = useQueryParameterState<AssignAdminsProps>();

  useEffect(() => {
    if (params.assignAdmins) {
      setCompanyAdminsModal(true);
    }
  }, [params.assignAdmins]);

  const handleInitialLocations = () => jobPosting?.locations?.map((location) => location.resourceId) || [];

  const handleInitialTextFields = (values: any) =>
    values
      ?.map((value: string) => value.replace(/,/g, ''))
      .join('\n')
      .toString() || '';

  const handlePublish = (jpResourceId: string) => {
    setLoadingContext(true);
    jobPostingService.publish(companyId, jpResourceId).then((res) => {
      if (res.ok && res.data) {
        handleUpdateJobPosting?.(res.data);
        dispatchMessage(t('shared.formSubmit.publishSuccess'), 'success');
        setJobPostingPublished(true);
        jobPostings.invalidateCache();
      } else {
        dispatchMessage(
          t('shared.formSubmit.publishError', {error: res.error?.extra_details || 'Undefined error'}).toString(),
          'error'
        );
      }
      formik.setSubmitting(false);
    });
    setTimeout(() => setLoadingContext(false), 1500);
  };

  const formik = useFormik({
    initialValues: {
      title: jobPosting?.title || '',
      departments: jobPosting?.departments?.map((dep) => dep.resourceId) || [],
      subdepartments:
        jobPosting?.departments
          ?.map((dep) => dep?.subDepartments || [])
          .flat()
          .map((sub) => sub.resourceId) || [],
      locations: handleInitialLocations(),
      remoteHiring: jobPosting?.remoteHiring || false,
      salary: {
        min: jobPosting?.salary?.min || '',
        max: jobPosting?.salary?.max || '',
      },
      workMode: jobPosting?.workMode || '',
      employmentType: jobPosting?.employmentType || [],
      degree: jobPosting?.degree || '',
      experience: {
        yearsOfExperience: jobPosting?.experience?.yearsOfExperience || 0,
        yearsOfLeadership: jobPosting?.experience?.yearsOfLeadership || 0,
      },
      languageSkills: jobPosting?.languageSkills || [],
      responsibilities: handleInitialTextFields(jobPosting?.responsibilities),
      requirements: handleInitialTextFields(jobPosting?.requirements),
      skills: jobPosting?.skills || [],
      companyAdminIds:
        jobPosting?.companyAdmins?.filter((admin) => admin.selected).map((admin) => admin.resourceId) || [],
    },
    validate: (values) => validateJobPostingForm(t, values),
    onSubmit: async (values) => {
      setLoadingContext(true);
      const handleResponse = (res: ApiResponse<JobPosting>) => {
        if (res.ok && res.data) {
          setJobPostingResourceId(res.data.resourceId || '');
          afterSubmit(res.data);
          dispatchMessage(t('shared.formSubmit.creationSuccess'), 'success');
          saveAndPublish && handlePublish(res.data.resourceId || '');
          setMode(FORM_MODE_EDIT);
          formik.setSubmitting(false);
          updateQueryParameters({assignAdmins: ''});
          setLoadingContext(false);
          return res.ok;
        } else {
          formik.setSubmitting(false);
          setLoadingContext(false);
          return res.error?.extra_details || 'Undefined error';
        }
      };

      if (mode === FORM_MODE_CREATE) {
        const res = await jobPostingService.create(companyId, values).then(handleResponse);

        if (typeof res === 'boolean') {
          dispatchMessage(t('shared.formSubmit.creationSuccess'), 'success');
        } else {
          dispatchMessage(t('shared.formSubmit.creationError', res), 'error');
        }
      } else if (mode === FORM_MODE_EDIT) {
        const res = await jobPostingService.update(companyId, jobPostingResourceId, values).then(handleResponse);
        if (typeof res === 'boolean') {
          if (params.assignAdmins) {
            onCancel();
          }
          dispatchMessage(t('shared.formSubmit.success'), 'success');
        } else {
          dispatchMessage(t('shared.formSubmit.error', res), 'error');
        }
      }
    },
  });

  return (
    <div>
      <EmpionPaper elevation={0} sx={{padding: '20px', backgroundImage: 'none'}}>
        <LoadingOverlay />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '20px',
          }}
        >
          <AssignedCompanyAdmins companyAdmins={jobPosting?.companyAdmins || []} />
          <Chip
            label={jobPostingPublished ? t('shared.published') : t('companyJobPostings.formButtons.draft')}
            sx={{
              fontSize: '14px',
              width: 'fit-content',
              backgroundColor: jobPostingPublished ? greenColors[40] : orangeColors[40],
              color: '#071112',
              fontWeight: 'bold',
            }}
          />
        </Box>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <HardFactsFields
              companyLocations={companyLocations}
              displayDefaultLocations={!!jobPosting?.locations}
              formik={formik}
            />
            <AboutTheJobFields
              formik={formik}
              modalOpen={companyAdminsModal}
              onClose={() => {
                setCompanyAdminsModal(false);
                if (params.assignAdmins) {
                  onCancel();
                  updateQueryParameters({assignAdmins: ''});
                }
              }}
            />
            <JobPostingFormMenu
              mode={mode}
              jobPostingPublished={jobPostingPublished}
              formik={formik}
              onCancel={onCancel}
              onPublish={handlePublish}
              setSaveAndPublish={setSaveAndPublish}
              jobPostingResourceId={jobPostingResourceId}
              assignAdmins={setCompanyAdminsModal}
            />
          </form>
        </FormikProvider>
      </EmpionPaper>
    </div>
  );
};
